import React, { forwardRef, useEffect, useRef, useState } from 'react'
import { Button } from 'chemistry'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useOutsideClick
} from '@chakra-ui/react'

import FilterIcon from '../../FilterButton/FilterIcon'
import { ListFilterItems } from '../ListFilter/ListFilterItems'
import { RangeFilterItem } from '../RangeFilter'
import { CheckListFilterItems } from '../CheckListFilter/CheckListFilterItems'
import { useIsFullyInViewport } from './useIsFullyInViewport'
import { BRICO } from 'client/common/utils'
import { isFlagInGA, pushFlagToDatalayer } from 'client/fragments/listerFragment/store/middleware/gtm'
import { SHOW_NEW_AVAILABILITY_FILTER_LABELS } from 'common/constants'
import { AVAILABILITY_FILTER_NAMES } from 'client/fragments/listerFragment/constants'
import { useSelector } from 'react-redux'

export const FILTERS_TOP_ID = 'filters_top'
export const FILTER_TOP_BUTTON_HEIGHT = 40
export const FILTER_TOP_DROPDOWN_MARGIN_TOP = 8
const filterTopButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  height: `${FILTER_TOP_BUTTON_HEIGHT}px`,
  p: '6px 12px',
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '0.25px',
  bgColor: 'grey.100',
  color: 'grey.900',
  borderRadius: 'lg',
  gap: '8px',
  _hover: {
    bgColor: 'grey.300'
  }
}

export const AllFiltersTopButton = ({ children, newVersionStyles = {}, isShown, ...props }) => (
  <Button
    {...{ ...filterTopButtonStyles, ...newVersionStyles, visibility: isShown ? 'visible' : 'hidden' }}
    variant='tertiary'
    position='static'
    {...props}
  >
    {children}
    <FilterIcon width='16px' height='16px' />
  </Button>
)

function FilterTopDropdownContents({
  filter,
  active,
  onChange,
  onClickStickyFilter,
  preventClick,
  rangeFilterToText,
  buttonActiveText,
  buttonInactiveText,
  format,
  lang,
  isParentCollapsed
}) {
  if (filter.items?.length === 0 && filter.type !== 'range') return null
  if (filter.type === 'list') {
    return (
      <ListFilterItems
        filter={filter}
        preventClick={preventClick}
        onChange={onChange}
        onClickStickyFilter={onClickStickyFilter}
        buttonActiveText={buttonActiveText}
        buttonInactiveText={buttonInactiveText}
        isParentCollapsed={isParentCollapsed}
        isDropdownFilter
        filterPosition='top'
        px='8px'
      />
    )
  }
  if (filter.type === 'range') {
    return (
      <RangeFilterItem
        filter={filter}
        onClickStickyFilter={onClickStickyFilter}
        active={active[filter.title]}
        toText={rangeFilterToText}
        onChange={onChange}
        filterPosition='top'
      />
    )
  }
  return (
    <CheckListFilterItems
      onChange={onChange}
      onClickStickyFilter={onClickStickyFilter}
      filter={filter}
      format={format}
      lang={lang}
      buttonActiveText={buttonActiveText}
      buttonInactiveText={buttonInactiveText}
      isParentCollapsed={isParentCollapsed}
      isDropdownFilter
      filterPosition='top'
    />
  )
}

export const FilterTopDropdownButton = forwardRef(
  ({ id, dropdownButtonRef, filter, children, setIsDropdownExpanded, upperCaseTitle, ...props }, ref) => {
    return (
      <Accordion ref={ref} id={id} allowToggle {...props}>
        <AccordionItem border='none'>
          {({ isExpanded }) => {
            if (setIsDropdownExpanded) {
              setIsDropdownExpanded(isExpanded)
            }
            return (
              <>
                <AccordionButton
                  ref={dropdownButtonRef}
                  {...filterTopButtonStyles}
                  bgColor={isExpanded ? 'grey.500' : 'grey.100'}
                  fontWeight={upperCaseTitle ? '600' : '400'}
                >
                  {upperCaseTitle ? filter.title.toUpperCase() : filter.title}
                  <AccordionIcon color='grey.900' />
                </AccordionButton>
                {children}
              </>
            )
          }}
        </AccordionItem>
      </Accordion>
    )
  }
)

export function FilterTopDropdown({
  id,
  filter,
  active,
  filterProcessedData: {
    onChange,
    preventClick,
    format,
    lang,
    buttonActiveText,
    buttonInactiveText,
    rangeFilterToText
  },
  onClickStickyFilter,
  maxDropdownHeight,
  upperCaseTitle,
  ...props
}) {
  const flagsToGA = useSelector(store => store.data?.flags?.flagsToGA)
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false)
  const ref = useRef()
  const dropdownButtonRef = useRef()
  const dropdownPanelRef = useRef()
  const { isFullyInViewport, setIsFullyInViewport } = useIsFullyInViewport(dropdownPanelRef)
  useOutsideClick({
    ref,
    handler: () => {
      if (isDropdownExpanded) {
        dropdownButtonRef?.current?.click()
      }
    }
  })

  useEffect(() => {
    if (!isFullyInViewport) {
      document.getElementById(FILTERS_TOP_ID).scrollIntoView({ behavior: 'smooth' })
      setIsFullyInViewport(true)
    }
  }, [isFullyInViewport, setIsFullyInViewport])

  return (
    <FilterTopDropdownButton
      ref={ref}
      id={id}
      setIsDropdownExpanded={setIsDropdownExpanded}
      onClick={() => {
        if (
          AVAILABILITY_FILTER_NAMES.includes(filter.name) &&
          format === BRICO &&
          isFlagInGA({ flagsToGA, ldFlag: SHOW_NEW_AVAILABILITY_FILTER_LABELS }) &&
          !isDropdownExpanded
        ) {
          pushFlagToDatalayer({
            ldFlag: 'availability-filter-labels',
            ldVariation: 'shown'
          })
        }
      }}
      dropdownButtonRef={dropdownButtonRef}
      filter={filter}
      upperCaseTitle={upperCaseTitle}
      {...props}
    >
      <AccordionPanel
        ref={dropdownPanelRef}
        p='8px'
        position='absolute'
        bg='white.0'
        marginTop='8px'
        borderRadius='lg'
        minWidth='296px'
        border='1px solid'
        borderColor='grey.100'
        boxShadow='0px 4px 6px -2px rgba(0, 0, 0, 0.15), 0px 10px 15px -3px rgba(0, 0, 0, 0.30)'
        maxHeight={maxDropdownHeight}
        overflowY='auto'
        overscrollBehaviorY='contain'
      >
        <FilterTopDropdownContents
          filter={filter}
          active={active}
          onChange={onChange}
          onClickStickyFilter={onClickStickyFilter}
          preventClick={preventClick}
          rangeFilterToText={rangeFilterToText}
          buttonActiveText={buttonActiveText}
          buttonInactiveText={buttonInactiveText}
          format={format}
          lang={lang}
          isParentCollapsed={!isDropdownExpanded}
        />
      </AccordionPanel>
    </FilterTopDropdownButton>
  )
}
